import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Search, Store, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import { isTiktokShopAuthorized } from '@/tiktok-platform/constants/tiktokRoutes';
import {
  normalizeTiktokShopType,
  shopTypeEquals,
  shopTypeLabel,
} from '@/utils/platformShopCatalog';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
} from '@/utils/platformShopScope';
import { localizedShopTypeLabel } from '@/i18n/shopTypeText';

/**
 * TikTok 店铺切换弹窗：
 * - 仅展示已授权店铺
 * - 仅可切换到与当前工作区同类型的店铺（半托管 / 自运营 / 全托管隔离）
 * - 同类型 ≥2 家时提供「全部」入口
 * - 表格展示：店铺名称、店铺类型、状态
 */
export default function TiktokShopSwitchModal({
  open,
  shops = [],
  activeShopId,
  workspaceShopType = '',
  loading = false,
  onSelect,
  onSelectAll,
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const typeName = (value) => localizedShopTypeLabel(t, value);
  const [keyword, setKeyword] = useState('');

  const activeShop = useMemo(
    () => (shops || []).find((s) => String(s.id) === String(activeShopId)) || null,
    [shops, activeShopId],
  );

  const activeShopType = useMemo(
    () => normalizeTiktokShopType(activeShop?.shopType)
      || normalizeTiktokShopType(workspaceShopType)
      || fixedUi("半托管"),
    [activeShop, workspaceShopType],
  );

  const allMode = isAllShopsScope(activeShopId);

  useEffect(() => {
    if (!open) return undefined;
    setKeyword('');
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const sameTypeAuthorizedShops = useMemo(
    () => (shops || []).filter((shop) => (
      isTiktokShopAuthorized(shop)
      && shopTypeEquals(shop?.shopType, activeShopType, 'TikTok')
    )),
    [shops, activeShopType],
  );

  const showAllOption = sameTypeAuthorizedShops.length >= 2;

  const filtered = useMemo(() => {
    const q = String(keyword || '').trim().toLowerCase();
    if (!q) return sameTypeAuthorizedShops;
    return sameTypeAuthorizedShops.filter((shop) => {
      const name = String(shop.shopName || '').toLowerCase();
      const id = String(shop.id || '').toLowerCase();
      const type = String(shopTypeLabel(shop.shopType, 'TikTok') || '').toLowerCase();
      return name.includes(q) || id.includes(q) || type.includes(q);
    });
  }, [sameTypeAuthorizedShops, keyword]);

  if (!open) return null;

  return createPortal(
    <div className="tiktok-shop-switch-modal" role="presentation" onClick={onClose}>
      <div
        className="tiktok-shop-switch-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tiktok-shop-switch-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="tiktok-shop-switch-modal__head">
          <div>
            <h2 id="tiktok-shop-switch-title">{t('tiktokSwitch.title')}</h2>
            <p>
              {t('tiktokSwitch.lead', { type: typeName(activeShopType), count: sameTypeAuthorizedShops.length })}
            </p>
          </div>
          <button
            type="button"
            className="tiktok-shop-switch-modal__close"
            onClick={onClose}
            aria-label={t('tiktokSwitch.close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="tiktok-shop-switch-modal__toolbar">
          <div className="tiktok-shop-switch-modal__search">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              className="tiktok-shop-switch-modal__search-input"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder={t('tiktokSwitch.searchPlaceholder')}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              autoFocus
            />
          </div>
        </div>

        <div className="tiktok-shop-switch-modal__list" aria-label={t('tiktokSwitch.listAria')}>
          {loading ? (
            <div className="tiktok-shop-switch-modal__empty">{t('tiktokGate.loadingShops')}</div>
          ) : shops.length === 0 ? (
            <div className="tiktok-shop-switch-modal__empty">
              <Store size={22} />
              <p>{t('tiktokSwitch.noTiktok')}</p>
              <Link
                to={`${IPE_WORKBENCH_PATH}?activeNav=platformShops`}
                className="tiktok-platform__link"
                onClick={onClose}
              >
                {t('tiktokSwitch.goManage')}
              </Link>
            </div>
          ) : sameTypeAuthorizedShops.length === 0 ? (
            <div className="tiktok-shop-switch-modal__empty">
              <p>{t('tiktokSwitch.noAuthorized')}</p>
              <span>{t('tiktokSwitch.switchOnly', { type: typeName(activeShopType) })}</span>
            </div>
          ) : (
            <table className="tiktok-shop-switch-modal__table">
              <thead>
                <tr>
                  <th scope="col">{t('shop.shopName')}</th>
                  <th scope="col">{t('shop.shopType')}</th>
                  <th scope="col">{t('shop.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {showAllOption && !String(keyword || '').trim() ? (
                  <tr
                    className={`tiktok-shop-switch-modal__row${allMode ? ' is-active' : ''}`}
                    onClick={() => onSelectAll?.() || onSelect?.({ id: PLATFORM_ALL_SHOPS_ID, shopName: '全部' })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelectAll?.() || onSelect?.({ id: PLATFORM_ALL_SHOPS_ID, shopName: '全部' });
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-pressed={allMode}
                  >
                    <td>
                      <span className="tiktok-shop-switch-modal__td-name">
                        <span className="tiktok-shop-switch-modal__name-text">
                          {t('tiktokGate.allAuthorized')}
                        </span>
                        {allMode ? (
                          <span className="tiktok-shop-switch-modal__current-tag">{t('tiktokSwitch.current')}</span>
                        ) : null}
                      </span>
                    </td>
                    <td>
                      <span className="tiktok-shop-switch-modal__type tiktok-shop-switch-modal__type--semi">
                        {typeName(activeShopType)}
                      </span>
                    </td>
                    <td>
                      <span className="tiktok-shop-switch-modal__status tiktok-shop-switch-modal__status--authorized">
                        {t('tiktokSwitch.shopCount', { count: sameTypeAuthorizedShops.length })}
                      </span>
                    </td>
                  </tr>
                ) : null}
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <div className="tiktok-shop-switch-modal__empty">
                        <p>{t('tiktokSwitch.noMatch')}</p>
                        <span>{t('tiktokSwitch.tryKeyword')}</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.map((shop) => {
                  const selected = !allMode && String(shop.id) === String(activeShopId);
                  const typeText = shopTypeLabel(shop.shopType, 'TikTok') || activeShopType;
                  const typeKey = normalizeTiktokShopType(shop.shopType) || activeShopType;
                  const typeMod = typeKey === '全托管'
                    ? 'full'
                    : typeKey === '自运营'
                      ? 'self'
                      : 'semi';
                  return (
                    <tr
                      key={shop.id}
                      className={`tiktok-shop-switch-modal__row${selected ? ' is-active' : ''}`}
                      onClick={() => onSelect?.(shop)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelect?.(shop);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-pressed={selected}
                    >
                      <td>
                        <span className="tiktok-shop-switch-modal__td-name">
                          <span className="tiktok-shop-switch-modal__name-text">
                            {shop.shopName || t('tiktokGate.shopFallback', { id: shop.id })}
                          </span>
                          {selected ? (
                            <span className="tiktok-shop-switch-modal__current-tag">{t('tiktokSwitch.current')}</span>
                          ) : null}
                        </span>
                      </td>
                      <td>
                        <span className={`tiktok-shop-switch-modal__type tiktok-shop-switch-modal__type--${typeMod}`}>
                          {typeName(typeText)}
                        </span>
                      </td>
                      <td>
                        <span className="tiktok-shop-switch-modal__status tiktok-shop-switch-modal__status--authorized">
                          {t('shop.authConnected')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
